import { API_URL } from "env";
import { mixLogOut } from "../services/mixin";

/**
 * API thông báo của người dùng đang đăng nhập.
 * GET /api/v1/be/notifications — trả về Laravel paginator (danh sách nằm trong data.data).
 */

export interface NotificationItem {
    id: number;
    recipient_id: number;
    type: string;
    title: string;
    content: string;
    related_id: number | null;
    related_type: string | null;
    // Với type = "new_message": dùng để mở đúng phòng chat + hội thoại khách
    channel_connection_id?: number | null;
    channel_customer_id?: number | null;
    is_read: boolean;
    read_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface NotificationPage {
    current_page: number;
    // Một số endpoint trả "items", số khác trả "data" — hỗ trợ cả hai.
    items?: NotificationItem[];
    data?: NotificationItem[];
    last_page?: number;
    per_page?: number;
    total: number;
    next_page_url?: string | null;
    prev_page_url?: string | null;
}

export interface NotificationListResponse {
    status: boolean;
    data: NotificationPage | null;
}

// Đánh dấu một thông báo là đã đọc: POST /api/v1/be/notifications/{id}/read
export async function markNotificationRead(id: number): Promise<{ status: boolean; msg?: string }> {
    try {
        const token = localStorage.getItem("token") || "";
        const res = await fetch(`${API_URL}/api/v1/be/notifications/${id}/read`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg };
        }
        return { status: json?.status ?? true, msg: json?.msg };
    } catch (error) {
        console.error("Failed to mark notification read:", error);
        return { status: false };
    }
}

// Đánh dấu TẤT CẢ thông báo chưa đọc của người đang đăng nhập là đã đọc:
// POST /api/v1/be/notifications/read-all
export async function markAllNotificationsRead(): Promise<{ status: boolean; msg?: string }> {
    try {
        const token = localStorage.getItem("token") || "";
        const res = await fetch(`${API_URL}/api/v1/be/notifications/read-all`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg };
        }
        return { status: json?.status ?? true, msg: json?.msg };
    } catch (error) {
        console.error("Failed to mark all notifications read:", error);
        return { status: false };
    }
}

export async function getNotifications(
    // type: lọc theo loại thông báo (new_message | staff_message_support | noti_staff |
    // noti_telesale | noti_branch_staff | noti_action_staff | call_failed)
    params: { is_read?: boolean; type?: string; limit?: number; page?: number } = {}
): Promise<NotificationListResponse> {
    try {
        const token = localStorage.getItem("token") || "";
        const q = new URLSearchParams();
        if (params.is_read !== undefined) q.append("is_read", String(params.is_read));
        if (params.type) q.append("type", params.type);
        if (params.limit) q.append("limit", String(params.limit));
        if (params.page) q.append("page", String(params.page));

        const response = await fetch(`${API_URL}/api/v1/be/notifications?${q.toString()}`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        return { status: !!json?.status, data: json?.data ?? null };
    } catch (error) {
        console.error("Failed to fetch notifications:", error);
        return { status: false, data: null };
    }
}
